'use client'

import clsx from 'clsx'
import { ReactNode, useState } from 'react'
import { useForm } from 'react-hook-form'

import Button from '../Button/Button'
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection'
import SelectInput from '../Form/SelectInput/SelectInput'
import Header from '../Header/Header'
import Link from '../Link/Link'

interface Category {
  categoryName: string
  categoryId: number
}

interface Item {
  question: string
  answer: string | ReactNode
  categoryName?: string
  categoryId?: number
}

interface FAQButton {
  moreText: string
}

export interface FAQProps {
  /**
   * Title of the FAQ
   */
  title: string | ReactNode
  /**
   * Subtitle of the FAQ
   */
  subtitle: string | ReactNode
  /**
   * list of questions and answer and associated category
   */
  items: Item[]
  /**
   * Button of the FAQ
   */
  button: FAQButton
  /**
   * Color variant of the container
   */
  variant: 'white' | 'blue'
  /**
   * Categories for filtering FAQ items
   */
  categories?: Category[]
  /**
   * Whether the button should redirect to FAQ page instead of toggling view
   */
  redirectToFaqPage?: boolean
}

export default function FAQ({
  title,
  subtitle,
  items,
  button,
  variant,
  categories,
  redirectToFaqPage = false,
}: FAQProps) {
  // Default to category 1 (How it Works) if categories exist
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    categories && categories.length > 0 ? 1 : null,
  )
  const [showAllItems, setShowAllItems] = useState(false)
  const { control } = useForm()

  // Filter items based on selected category
  const filteredItems = selectedCategoryId
    ? items.filter((item) => item.categoryId === selectedCategoryId)
    : items

  // When showAllItems is true, show all items from all categories
  // Otherwise, show filtered items limited to 5
  const displayItems = showAllItems
    ? items // Show all items from all categories
    : filteredItems.slice(0, 5) // Show limited items from selected category

  // Create dropdown options
  const dropdownOptions = categories
    ? [
        ...categories.map((category) => ({
          label: category.categoryName,
          value: category.categoryId.toString(),
        })),
      ]
    : []

  return (
    <section
      className={clsx(
        'flex justify-between flex-col lg:flex-row px-[1.78125rem] lg:px-20 py-8 lg:py-16 rounded-2xl w-full gap-x-[5.75rem]',
        variant === 'white' ? 'bg-white' : 'bg-theme-accent-4',
      )}
    >
      <div className="lg:w-[44.5%]">
        <Header
          as="h2"
          className="type-headline-2 font-normal [&_b]:type-headline-1 mb-4 text-center md:text-left"
          overrideClassName
        >
          {title}
        </Header>
        <p className="type-body-4 mb-4 lg:mb-12 text-center md:text-left">
          {subtitle}
        </p>
        {categories && categories.length > 0 && (
          <div className="mb-6">
            <SelectInput
              control={control}
              name="categoryFilter"
              label="Filter by Category"
              placeholder="Select a category"
              options={dropdownOptions}
              onChange={(value) => {
                setSelectedCategoryId(value ? parseInt(value as string) : null)
                setShowAllItems(false) // Reset to show limited items when changing category
              }}
              className="max-w-xs"
            />
          </div>
        )}
      </div>
      <div className="lg:w-[55.5%]">
        {displayItems.map(({ question, answer }) => (
          <CollapsibleSection
            title={question}
            overrideSectionClassName
            buttonClassName="text-left px-0 py-4 lg:py-[1.625rem] type-body-5"
            sectionClassName="border-b border-neutral-700 mb-2 lg:mb-3"
            panelClassName="px-0 pb-[1.5625rem] type-body-8"
            key={question}
          >
            {answer}
          </CollapsibleSection>
        ))}
        <div className="sm-max:text-center">
          {redirectToFaqPage ? (
            <Link href="/faq">
              <Button className="mt-6 lg:mt-12">{button.moreText}</Button>
            </Link>
          ) : (
            <Button
              className="mt-6 lg:mt-12"
              onClick={() => setShowAllItems(!showAllItems)}
            >
              {showAllItems ? 'Show Less' : button.moreText}
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}
